import type { XYPoint } from "@/lib/tool-kit/calculus/data-points";

/**
 * Linear and bilinear interpolation (Burden & Faires, Numerical Analysis, §3.1;
 * Press et al., Numerical Recipes, §3.6).
 *
 *     y = y₁ + (x − x₁)(y₂ − y₁)/(x₂ − x₁)          the line through (x₁, y₁) and (x₂, y₂)
 *     x = x₁ + (y − y₁)(x₂ − x₁)/(y₂ − y₁)          the inverse: x for a given y
 *
 * Outside [x₁, x₂] the same line gives an extrapolation, which is reported as such.
 */

/** Position of x between x₁ (0) and x₂ (1); outside [0, 1] means extrapolation. */
export function fraction(x1: number, x2: number, x: number): number {
  return (x - x1) / (x2 - x1);
}

/** y on the line through two points; NaN if x₁ = x₂. */
export function lerp(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x: number
): number {
  return y1 + fraction(x1, x2, x) * (y2 - y1);
}

/** x at which the line through two points has height y; NaN for a horizontal line. */
export function inverseLerp(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  y: number
): number {
  return y2 === y1 ? Number.NaN : x1 + ((y - y1) * (x2 - x1)) / (y2 - y1);
}

export type Extrapolation = "below" | "above" | null;

export interface TableInterpolation {
  value: number;
  /** The two data points whose line was used. */
  segment: [XYPoint, XYPoint];
  extrapolated: Extrapolation;
  /** x coincides with a data point. */
  exact: boolean;
}

/** Collapse repeated x-values (keeping the first) and sort. */
export function distinctSorted(points: XYPoint[]): XYPoint[] {
  const sorted = [...points].sort((p, q) => p.x - q.x);
  return sorted.filter((p, i) => i === 0 || p.x !== sorted[i - 1].x);
}

/**
 * Interpolate within a table by the segment that contains x; outside the data, extend
 * the first or last segment. Null if fewer than two distinct x-values.
 */
export function interpolateTable(
  points: XYPoint[],
  x: number
): TableInterpolation | null {
  const data = distinctSorted(points);
  if (data.length < 2) {
    return null;
  }
  const first = data[0];
  const last = data.at(-1) ?? first;
  let extrapolated: Extrapolation = null;
  let at = 0;
  if (x < first.x) {
    extrapolated = "below";
  } else if (x > last.x) {
    extrapolated = "above";
    at = data.length - 2;
  } else {
    at = Math.max(0, data.findIndex((p) => p.x >= x) - 1);
    if (at < 0) {
      at = 0;
    }
    while (at < data.length - 2 && data[at + 1].x < x) {
      at += 1;
    }
  }
  const [p, q] = [data[at], data[at + 1]];
  return {
    value: lerp(p.x, p.y, q.x, q.y, x),
    segment: [p, q],
    extrapolated,
    exact: data.some((d) => d.x === x),
  };
}

/** Every x in the table's range where the piecewise-linear curve reaches height y. */
export function inverseTable(points: XYPoint[], y: number): number[] {
  const data = distinctSorted(points);
  const found: number[] = [];
  for (let i = 0; i + 1 < data.length; i += 1) {
    const [p, q] = [data[i], data[i + 1]];
    const lo = Math.min(p.y, q.y);
    const hi = Math.max(p.y, q.y);
    if (y < lo || y > hi) {
      continue;
    }
    const x = p.y === q.y ? p.x : inverseLerp(p.x, p.y, q.x, q.y, y);
    if (
      !found.some((f) => Math.abs(f - x) < 1e-12 * Math.max(1, Math.abs(x)))
    ) {
      found.push(x);
    }
  }
  return found;
}

export interface BilinearInput {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  /** f(x₁, y₁), f(x₂, y₁), f(x₁, y₂), f(x₂, y₂). */
  q11: number;
  q21: number;
  q12: number;
  q22: number;
}

export interface BilinearResult {
  value: number;
  /** Interpolated along x at y = y₁ and at y = y₂. */
  atY1: number;
  atY2: number;
  /** Fractions of the way across the x and y ranges. */
  tx: number;
  ty: number;
  extrapolated: boolean;
}

/** Interpolate along x on both rows, then along y between the two results. */
export function bilinear(
  input: BilinearInput,
  x: number,
  y: number
): BilinearResult {
  const { x1, x2, y1, y2, q11, q21, q12, q22 } = input;
  const tx = fraction(x1, x2, x);
  const ty = fraction(y1, y2, y);
  const atY1 = q11 + tx * (q21 - q11);
  const atY2 = q12 + tx * (q22 - q12);
  return {
    value: atY1 + ty * (atY2 - atY1),
    atY1,
    atY2,
    tx,
    ty,
    extrapolated: tx < 0 || tx > 1 || ty < 0 || ty > 1,
  };
}
